import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Collapse,
  TableCell,
  TableContainer,
  TableRow,
} from "@material-ui/core";
import HearingIcon from "@material-ui/icons/Hearing";
import NoteIcon from "@material-ui/icons/Note";
import ListeningDialog from "../listenings/ListeningDialog";
import { buildBluetoothSpeakerListeningOwner } from "../listenings/listeningOwners";
import CalibrationNotesDialog from "../notes/CalibrationNotesDialog";
import useCalibrationNotes from "../notes/useCalibrationNotes";
import ActionCountCell from "../shared/ActionCountCell";
import RawCalibrationDetailsTable from "../shared/RawCalibrationDetailsTable";
import { convertDate, convertHours } from "../utils/dateConverter";
import {
  addBluetoothSpeakerNote,
  bluetoothSpeakerListeningApi,
  deleteBluetoothSpeakerNote,
  getBluetoothSpeakerNotes,
  updateBluetoothSpeakerNote,
} from "../utils/NetworkHandler";

function getInitialNotesCount(rowData) {
  if (rowData?.notesCount != null) {
    return rowData.notesCount;
  }
  if (rowData?.notes?.size != null) {
    return rowData.notes.size;
  }
  return 0;
}

export default function BluetoothSpeakerCalibrationRow({
  rowData,
  isDetailedData,
  updateData,
  columnCount,
  removeHandler,
}) {
  const id = rowData?.id;
  const [open, setOpen] = useState(false);
  const [listeningDialogOpen, setListeningDialogOpen] = useState(false);
  const [listeningCount, setListeningCount] = useState(
    rowData?.listeningCount ?? 0,
  );

  useEffect(() => {
    setListeningCount(rowData?.listeningCount ?? 0);
  }, [rowData?.listeningCount]);

  const listeningOwner = useMemo(
    () => buildBluetoothSpeakerListeningOwner(rowData),
    [rowData],
  );

  const initialNotesCountsByOwnerId = useMemo(
    () => ({ [id]: getInitialNotesCount(rowData) }),
    [id, rowData],
  );

  const notes = useCalibrationNotes({
    getOwnerId: (calibration) => calibration?.id,
    getNotes: getBluetoothSpeakerNotes,
    addNote: addBluetoothSpeakerNote,
    updateNote: updateBluetoothSpeakerNote,
    deleteNote: deleteBluetoothSpeakerNote,
    initialCountsByOwnerId: initialNotesCountsByOwnerId,
    onNotesChanged: async () => {
      if (updateData) {
        await updateData();
      }
    },
  });

  const notesCount = notes.getNotesCount(rowData, getInitialNotesCount(rowData));

  const handleListeningCreated = () => {
    setListeningCount((previous) => previous + 1);
    if (updateData) {
      Promise.resolve(updateData()).catch((error) => {
        console.error("updateData failed after BT listening create", error);
      });
    }
  };

  const handleListeningDeleted = () => {
    setListeningCount((previous) => Math.max(0, previous - 1));
    if (updateData) {
      Promise.resolve(updateData()).catch((error) => {
        console.error("updateData failed after BT listening delete", error);
      });
    }
  };

  return (
    <>
      <TableRow>
        <TableCell style={{ paddingLeft: 30 }}>
          {convertHours(rowData?.createdAt)}
        </TableCell>
        <TableCell>{convertDate(rowData?.createdAt)}</TableCell>
        <TableCell>{rowData?.speakerModel}</TableCell>
        <TableCell>{rowData?.model}</TableCell>
        <TableCell>{rowData?.brand}</TableCell>
        <TableCell>{rowData?.manufacturer}</TableCell>
        <TableCell>{rowData?.deviceId}</TableCell>
        <TableCell>{rowData?.calibratorName}</TableCell>

        <ActionCountCell
          label="Notatki"
          icon={NoteIcon}
          onClick={() => notes.openNotesDialog(rowData)}
          count={notesCount}
          hideZeroCount
        />

        <ActionCountCell
          label="Odsłuchy"
          icon={HearingIcon}
          onClick={() => setListeningDialogOpen(true)}
          count={listeningCount}
        />

        <TableCell align="center">
          <Button
            onClick={() => setOpen((previous) => !previous)}
            style={{ marginRight: 8 }}
          >
            Szczegóły
          </Button>
          <Button onClick={() => removeHandler(id)}>Usuń</Button>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={columnCount}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <TableContainer>
              <RawCalibrationDetailsTable
                calibrations={rowData?.calibrations}
                isDetailedData={isDetailedData}
                showMicrophoneSignal={false}
              />
            </TableContainer>
          </Collapse>
        </TableCell>
      </TableRow>

      <ListeningDialog
        open={listeningDialogOpen}
        onClose={() => setListeningDialogOpen(false)}
        ownerCalibrationId={listeningOwner.ownerCalibrationId}
        averagingLevel={null}
        defaultDeviceId={listeningOwner.deviceId}
        defaultModelName={listeningOwner.model}
        defaultBrandName={listeningOwner.brand}
        availableFrequencies={listeningOwner.availableFrequencies}
        onListeningCreated={handleListeningCreated}
        onListeningDeleted={handleListeningDeleted}
        listeningApi={bluetoothSpeakerListeningApi}
        modeLabel="GŁOŚNIK_BT"
        isBluetoothSpeaker
      />

      <CalibrationNotesDialog
        open={notes.open}
        onClose={notes.closeNotesDialog}
        title="Notatki kalibracji głośnika BT"
        notes={notes.notes}
        loading={notes.loadingNotes}
        error={notes.notesError}
        editingNoteId={notes.editingNoteId}
        editingText={notes.editingText}
        onEditingTextChange={notes.setEditingText}
        saving={notes.saving}
        deletingId={notes.deletingId}
        newNoteText={notes.newNoteText}
        onNewNoteTextChange={notes.setNewNoteText}
        adding={notes.adding}
        onStartEdit={notes.startEdit}
        onCancelEdit={notes.cancelEdit}
        onSaveEdit={notes.saveEdit}
        onDeleteNote={notes.handleDeleteNote}
        onStartAdd={notes.startAdd}
        onCancelAdd={notes.cancelAdd}
        onSaveNew={notes.saveNew}
        emptyText="Brak notatek."
      />
    </>
  );
}
